import React from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Divider, TextField, Typography } from '@mui/material';
import Contact from '../../../models/Contact';
import { StyledButton } from '../../styles/StyledButtons';
import { StyledLabel } from '../../styles/styles';
import { PreviewAssistTranscriptMessage } from './veraAdminChatAssistPromptSandboxUtils';

interface AdminAssistTranscriptSectionProps {
  selectedContact: Contact | null;
  transcript: PreviewAssistTranscriptMessage[];
  investorComposeText: string;
  adminComposeText: string;
  onInvestorComposeChange: (value: string) => void;
  onAdminComposeChange: (value: string) => void;
  onAddMessage: (role: 'investor' | 'admin', text: string) => void;
  onResetTranscript: () => void;
}

export default function AdminAssistTranscriptSection({
  selectedContact,
  transcript,
  investorComposeText,
  adminComposeText,
  onInvestorComposeChange,
  onAdminComposeChange,
  onAddMessage,
  onResetTranscript,
}: AdminAssistTranscriptSectionProps) {
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <StyledLabel stacked>Sandbox Chat Transcript</StyledLabel>
        <StyledButton
          size="small"
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={onResetTranscript}
          disabled={transcript.length === 0}
        >
          Reset transcript
        </StyledButton>
      </Box>

      {!selectedContact ? (
        <Box
          sx={{
            p: 2,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'warning.main',
            bgcolor: 'warning.light',
          }}
        >
          <Typography variant="body2" sx={{ color: 'warning.contrastText', fontWeight: 500 }}>
            Search and select a contact above to build a test transcript.
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              mb: 2,
              maxHeight: 260,
              overflowY: 'auto',
              p: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: '#f6f7f9',
            }}
          >
            {transcript.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Add messages as the investor or admin to simulate a support conversation.
              </Typography>
            ) : (
              transcript.map((message) => (
                <Box
                  key={message.id}
                  sx={{
                    alignSelf: message.senderRole === 'admin' ? 'flex-end' : 'flex-start',
                    maxWidth: '85%',
                    p: 1.25,
                    borderRadius:
                      message.senderRole === 'admin' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                    bgcolor: message.senderRole === 'admin' ? 'primary.main' : '#fff',
                    color: message.senderRole === 'admin' ? 'primary.contrastText' : 'text.primary',
                    border: message.senderRole === 'investor' ? '1px solid #e3e8ef' : 'none',
                  }}
                >
                  <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 0.5 }}>
                    {message.senderRole === 'investor' ? 'Investor' : 'Admin'} ·{' '}
                    {message.senderName}
                  </Typography>
                  <Typography variant="body2">{message.text}</Typography>
                </Box>
              ))
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <Box
              sx={{ flex: 1, minWidth: 260, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}
            >
              <Typography variant="body2" sx={{ pt: 1, fontWeight: 500, whiteSpace: 'nowrap' }}>
                As Investor:
              </Typography>
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                value={investorComposeText}
                onChange={(event) => onInvestorComposeChange(event.target.value)}
                placeholder="Type an investor message…"
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    onAddMessage('investor', investorComposeText);
                  }
                }}
              />
            </Box>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ display: { xs: 'none', md: 'block' } }}
            />
            <Divider sx={{ display: { xs: 'block', md: 'none' }, width: '100%' }} />

            <Box
              sx={{ flex: 1, minWidth: 260, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}
            >
              <Typography variant="body2" sx={{ pt: 1, fontWeight: 500, whiteSpace: 'nowrap' }}>
                As Admin:
              </Typography>
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                value={adminComposeText}
                onChange={(event) => onAdminComposeChange(event.target.value)}
                placeholder="Type an admin reply…"
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    onAddMessage('admin', adminComposeText);
                  }
                }}
              />
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Press Enter in either field to add that message to the transcript. Shift+Enter for a new
            line.
          </Typography>
        </>
      )}
    </Box>
  );
}
